/**
 * StickerInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { StickerMedias } from './sticker_medias'

/**
 * StickerInfo
 */
export interface StickerInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** 表情来源（可选值：user_upload=用户上传; klipy=Klipy API; tenor=Tenor API; system=系统预设） */
  source?: string
  /** 来源平台的原始ID（API来源时使用，用于去重和更新） */
  source_id?: string
  /** 表情类型（可选值：gif=GIF动图; sticker=静态贴纸; meme=表情包） */
  type?: string
  /** 表情标题 */
  title?: string
  /** 表情描述 */
  description?: string
  /** 标签列表，用于搜索匹配 */
  tags?: string
  /** 关联的文件ID（仅用户上传时非空，关联 file_info 表） */
  file_id?: string
  /** 主要展示URL（所有来源都填充） */
  url?: string
  /** 缩略图URL（所有来源都填充） */
  thumbnail_url?: string
  /** 宽度（像素） */
  width?: number
  /** 高度（像素） */
  height?: number
  /** 文件大小（字节） */
  size?: number
  /** 时长（毫秒），GIF/视频专用 */
  duration?: number
  /** 是否动图（GIF/视频） */
  is_animated?: boolean
  /** 是否有声音 */
  has_audio?: boolean
  /** 多格式媒体信息（仅API来源），包含preview/gif/mp4/webm/thumbnail */
  media?: StickerMedias
  /** 创建者用户ID（仅用户上传时非空） */
  creator_id?: string
}

/**
 * StickerInfo Schema
 */
export const StickerInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    source: {
      description:
        '表情来源（可选值：user_upload=用户上传; klipy=Klipy API; tenor=Tenor API; system=系统预设）',
      format: 'cons.StickerSource',
      type: 'string',
      label: '表情来源',
    },
    source_id: {
      description: '来源平台的原始ID（API来源时使用，用于去重和更新）',
      format: 'string',
      type: 'string',
      label: '来源平台的原始ID',
    },
    type: {
      description: '表情类型（可选值：gif=GIF动图; sticker=静态贴纸; meme=表情包）',
      format: 'cons.StickerType',
      type: 'string',
      label: '表情类型',
    },
    title: {
      description: '表情标题',
      format: 'string',
      type: 'string',
      label: '表情标题',
    },
    description: {
      description: '表情描述',
      format: 'string',
      type: 'string',
      label: '表情描述',
    },
    tags: {
      description: '标签列表，用于搜索匹配',
      format: 'string',
      type: 'string',
      label: '标签列表',
    },
    file_id: {
      description: '关联的文件ID（仅用户上传时非空，关联 file_info 表）',
      format: 'string',
      type: 'string',
      label: '关联的文件ID',
    },
    url: {
      description: '主要展示URL（所有来源都填充）',
      format: 'string',
      type: 'string',
      label: '主要展示URL',
    },
    thumbnail_url: {
      description: '缩略图URL（所有来源都填充）',
      format: 'string',
      type: 'string',
      label: '缩略图URL',
    },
    width: {
      description: '宽度（像素）',
      format: 'int',
      type: 'integer',
      label: '宽度',
    },
    height: {
      description: '高度（像素）',
      format: 'int',
      type: 'integer',
      label: '高度',
    },
    size: {
      description: '文件大小（字节）',
      format: 'int64',
      type: 'integer',
      label: '文件大小',
    },
    duration: {
      description: '时长（毫秒），GIF/视频专用',
      format: 'int',
      type: 'integer',
      label: '时长',
    },
    is_animated: {
      description: '是否动图（GIF/视频）',
      format: 'bool',
      type: 'boolean',
      label: '是否动图',
    },
    has_audio: {
      description: '是否有声音',
      format: 'bool',
      type: 'boolean',
      label: '是否有声音',
    },
    media: {
      $ref: 'StickerMedias',
      description: '多格式媒体信息（仅API来源），包含preview/gif/mp4/webm/thumbnail',
      label: '多格式媒体信息',
    },
    creator_id: {
      description: '创建者用户ID（仅用户上传时非空）',
      format: 'string',
      type: 'string',
      label: '创建者用户ID',
    },
  },
  type: 'object',
}
